// vcfUtils.js

const formatPronounForVCard = (pronoun) => {
  switch (pronoun) {
    case 'he': return 'He/Him/His';
    case 'she': return 'She/Her/Hers';
    case 'they': return 'They/Them/Theirs';
    case 'ze': return 'Ze/Zir/Zirs';
    case 'custom': return 'Custom pronouns'; // Should not normally occur with new implementation
    case '': return '';
    default:
      // If it's not one of the predefined options, it's a custom value
      return pronoun; // Return the custom pronoun directly
  }
};


// Helper function to format phone numbers for international compatibility
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Strip all non-digit characters except + sign
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // If it's already an international format (starts with +), return as is
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If it's not in international format, just return the cleaned number
  // This preserves the number without adding any country code
  return cleaned;
};

export const generateVCardContent = (cardData) => {
  const now = new Date().toISOString();
  const timestamp = now.split('T')[0]; // YYYY-MM-DD format

  // Start building vCard content with enhanced metadata
  let vCardContent = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `REV:${now}`,
    `PRODID:-//InTouchWe//Digital Business Card//EN`,
    `UID:intouchwe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  ];

  // Add name with proper formatting
  if (cardData.contact_info?.fullName) {
    const fullName = cardData.contact_info.fullName.trim();
    // Split name for proper N field format (Last;First;Middle;Prefix;Suffix)
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    vCardContent.push(`N;CHARSET=utf-8:${lastName};${firstName};;;`);
    vCardContent.push(`FN;CHARSET=utf-8:${fullName}`);
  }

  // Add organization with enhanced formatting
  if (cardData.contact_info?.company) {
    vCardContent.push(`ORG;CHARSET=utf-8:${cardData.contact_info.company}`);
  }

  // Add job title
  if (cardData.contact_info?.jobTitle) {
    vCardContent.push(`TITLE;CHARSET=utf-8:${cardData.contact_info.jobTitle}`);
  }

  // Add source information for InTouchWe branding
  vCardContent.push(`SOURCE:Shared via InTouchWe Digital Business Cards`);
  vCardContent.push(`X-INTOUCHWE:true`);
  vCardContent.push(`X-SHARED-DATE:${timestamp}`);

  // Prepare note content - combine bio and pronouns if available
  let noteContent = [];

  // Add pronouns to note if available
  if (cardData.contact_info?.pronoun) {
    const pronounText = formatPronounForVCard(cardData.contact_info.pronoun);
    if (pronounText) {
      noteContent.push(`Pronouns: ${pronounText}`);
    }
  }

  // Add bio to note if available
  if (cardData.bio) {
    noteContent.push(cardData.bio);
  }

  // Add combined note if we have any content
  if (noteContent.length > 0) {
    // Join with line breaks and escape any special chars
    const escapedNote = noteContent.join('\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
    vCardContent.push(`NOTE;CHARSET=utf-8:${escapedNote}`);
  }

  // Add custom X-field for pronouns to ensure better compatibility
  if (cardData.contact_info?.pronoun) {
    const pronounText = formatPronounForVCard(cardData.contact_info.pronoun);
    if (pronounText) {
      vCardContent.push(`X-PRONOUNS:${pronounText}`);
    }
  }

  // Add contact details
  if (cardData.contact_info?.email) {
    vCardContent.push(`EMAIL;TYPE=INTERNET,HOME:${cardData.contact_info.email}`);
  }

  // Handle phone numbers with proper formatting for WhatsApp compatibility
  if (cardData.contact_info?.phoneNumber) {
    const formattedPhone = formatPhoneNumber(cardData.contact_info.phoneNumber);
    // Add multiple types for better app compatibility
    vCardContent.push(`TEL;TYPE=CELL,VOICE:${formattedPhone}`);
  }

  // Check for phoneNumbers array used in newer contact structure
  if (cardData.contact_info?.phoneNumbers && Array.isArray(cardData.contact_info.phoneNumbers)) {
    cardData.contact_info.phoneNumbers.forEach(phone => {
      if (phone.number) {
        const formattedPhone = formatPhoneNumber(phone.number);
        let type = 'CELL,VOICE';
        if (phone.label && phone.label.toLowerCase() === 'work') {
          type = 'WORK,VOICE';
        } else if (phone.label && phone.label.toLowerCase() === 'home') {
          type = 'HOME,VOICE';
        }
        vCardContent.push(`TEL;TYPE=${type}:${formattedPhone}`);
      }
    });
  }

  // Add location/address
  if (cardData.contact_info?.location) {
    vCardContent.push(`ADR;TYPE=HOME;CHARSET=utf-8:;;;;;;${cardData.contact_info.location}`);
  }

  // Add photo if available
  if (cardData.profilePictureUrl) {
    vCardContent.push(`PHOTO;VALUE=URI:${cardData.profilePictureUrl}`);
  }

  // Add connection date
  vCardContent.push(`X-ABDATE;TYPE=Date connected:${now.split('T')[0]}`);

  // End vCard
  vCardContent.push('END:VCARD');

  return vCardContent.join('\r\n');
};

export const downloadVCard = (cardData) => {
  // Generate vCard content
  const vCardContent = generateVCardContent(cardData);

  // Create blob with vCard content
  const blob = new Blob([vCardContent], { type: 'text/vcard' });
  const url = window.URL.createObjectURL(blob);

  // Create download link
  const downloadLink = document.createElement('a');
  downloadLink.href = url;

  // Set filename - use contact name or default
  const fileName = cardData.contact_info?.fullName?.replace(/\s+/g, '_') || 'contact';
  downloadLink.download = `${fileName}.vcf`;

  // Trigger download
  document.body.appendChild(downloadLink);
  downloadLink.click();

  // Cleanup
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(url);
};